import { ChangeEventHandler, useState } from "react";
import produce from "immer";
import { useTranslation } from "react-i18next";
import { Box, TextField, Typography } from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import { isBlank } from "utils/string";

import { useRuleContext } from "../../../context/ruleContext";
import { Field, FormFields } from "../../../../common/FormFields";
import { ErrorMessage } from "../../Actions/Styled";

import { SupervisorNotification } from "@dashboard-v3/api";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";
import AutocompleteSearchInput from "components/AutocompleteSearchInput";
import useNotificationVariables from "pages/Rules/hooks/useNotificationVariables";
import { getApprovalSteps } from "pages/Rules/Form/context/ruleSelectors";

export default function SupervisorSettings() {
  const { variables, replaceIdsWithLabels, replaceLabelsWithIds } =
    useNotificationVariables({
      ruleType: "REQUEST_APPROVAL",
      notificationType: "SUPERVISOR",
    });
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { supervisorNotification } = getApprovalSteps(state.rule);
  const [isEnabled, setIsEnabled] = useState(Boolean(supervisorNotification));
  const recipient = t("approvalSteps.recipientType.supervisor");
  const titleError = isBlank(supervisorNotification?.title);
  const titleMaxLength =
    supervisorNotification?.title?.length > NOTIFICATION_LIMITS.TITLE;
  const subjectError = isBlank(supervisorNotification?.subject);
  const subjectMaxLength =
    supervisorNotification?.subject?.length > NOTIFICATION_LIMITS.EMAIL_SUBJECT;
  const bodyError = isBlank(supervisorNotification?.bodyText);
  const bodyMaxLength =
    supervisorNotification?.bodyText?.length > NOTIFICATION_LIMITS.BODY_TEXT;
  const accessLinkError = isBlank(supervisorNotification?.accessLinkLabel);
  const approveLinkError = isBlank(supervisorNotification?.approveLinkLabel);
  const rejectLinkError = isBlank(supervisorNotification?.rejectLinkLabel);
  const linkLabelDefaults = {
    accessLinkLabel: t("approvalSteps.supervisorEmail.access"),
    approveLinkLabel: t("approvalSteps.supervisorEmail.approve"),
    rejectLinkLabel: t("approvalSteps.supervisorEmail.reject"),
  };

  const updateSettings = (update: Partial<SupervisorNotification>) => {
    dispatch({
      type: "updateStep",
      payload: {
        step: "approvalSteps",
        changes: { supervisorNotification: update },
      },
    });
  };

  const handleNotificationText: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, value } = e.target;
    const update = produce(supervisorNotification, draft => {
      draft[name] = value;
    });

    updateSettings(update);
  };

  const handleNotificationLinks: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, checked } = e.target;
    const update = produce(supervisorNotification, draft => {
      const linkLabelKey = `${name}Label`;
      draft[name] = checked;

      if (checked && !draft[linkLabelKey]) {
        draft[linkLabelKey] = linkLabelDefaults[linkLabelKey];
      }

      if (!checked) {
        draft[linkLabelKey] = "";
      }
    });

    updateSettings(update);
  };

  function handleNotificationBody(value: string) {
    const update = produce(supervisorNotification, draft => {
      draft.bodyText = replaceLabelsWithIds(value);
    });

    updateSettings(update);
  }

  const onEnableNotification: ChangeEventHandler<HTMLInputElement> = e => {
    const { checked } = e.target;
    setIsEnabled(checked);

    if (!checked) return updateSettings(null);

    updateSettings({
      accessLink: false,
      accessLinkLabel: t("approvalSteps.supervisorEmail.access"),
      approveLink: true,
      approveLinkLabel: t("approvalSteps.supervisorEmail.approve"),
      bodyText: t("approvalSteps.supervisorEmail.body"),
      rejectLink: true,
      rejectLinkLabel: t("approvalSteps.supervisorEmail.reject"),
      subject: t("approvalSteps.supervisorEmail.title"),
      title: t("approvalSteps.supervisorEmail.title"),
    });
  };

  return (
    <Box mb="20px" mt="15px">
      <SwitchField
        label={t("approvalSteps.enableLabel", { recipient })}
        value={isEnabled}
        onChange={onEnableNotification}
        style={{ marginBottom: isEnabled ? 10 : 0 }}
        description={
          !isEnabled &&
          t("approvalSteps.supervisorNotification.enableDescription")
        }
      />
      {isEnabled && (
        <Box padding="8px 16px 16px">
          <FormFields>
            <Field>
              <TextField
                name="title"
                variant="outlined"
                label={t("approvalSteps.title", { recipient })}
                value={supervisorNotification.title}
                onChange={handleNotificationText}
                error={titleError || titleMaxLength}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.TITLE }}
              />
              <ErrorMessage
                isError={titleError}
                wording={t("approvalSteps.fieldRequired")}
              />
              <ErrorMessage
                isError={titleMaxLength}
                wording={t("approvalSteps.fieldMaxLength", {
                  max: NOTIFICATION_LIMITS.TITLE,
                })}
              />
            </Field>
            <Field>
              <TextField
                name="subject"
                variant="outlined"
                label={t("approvalSteps.subject", { recipient })}
                value={supervisorNotification.subject}
                onChange={handleNotificationText}
                error={subjectError || subjectMaxLength}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.EMAIL_SUBJECT }}
              />
              <ErrorMessage
                isError={subjectError}
                wording={t("approvalSteps.fieldRequired")}
              />
              <ErrorMessage
                isError={subjectMaxLength}
                wording={t("approvalSteps.fieldMaxLength", {
                  max: NOTIFICATION_LIMITS.EMAIL_SUBJECT,
                })}
              />
            </Field>
            <Field>
              <Typography
                variant="caption"
                color={bodyError ? "error" : "textSecondary"}
              >
                {t("approvalSteps.bodyText", { recipient })}
              </Typography>
              <AutocompleteSearchInput
                name="bodyText"
                multiple
                rows={5}
                fullWidth
                variables={variables}
                error={bodyError || bodyMaxLength}
                maxLength={NOTIFICATION_LIMITS.BODY_TEXT}
                value={replaceIdsWithLabels(supervisorNotification.bodyText)}
                onChange={handleNotificationBody}
              />
              <ErrorMessage
                isError={bodyError}
                wording={t("approvalSteps.fieldRequired")}
              />
              <ErrorMessage
                isError={bodyMaxLength}
                wording={t("approvalSteps.fieldMaxLength", {
                  max: NOTIFICATION_LIMITS.BODY_TEXT,
                })}
              />
            </Field>
            <SwitchField
              name="accessLink"
              label={t("approvalSteps.accessLink", { recipient })}
              description={t("approvalSteps.accessLinkDesc")}
              value={supervisorNotification.accessLink}
              onChange={handleNotificationLinks}
            />
            {supervisorNotification.accessLink && (
              <Field>
                <TextField
                  name="accessLinkLabel"
                  variant="outlined"
                  label={t("approvalSteps.accessLinkLabel")}
                  value={supervisorNotification.accessLinkLabel}
                  onChange={handleNotificationText}
                  error={accessLinkError}
                  inputProps={{ maxLength: NOTIFICATION_LIMITS.CTA_LABEL }}
                />
                <ErrorMessage
                  isError={accessLinkError}
                  wording={t("approvalSteps.fieldRequired")}
                />
              </Field>
            )}

            <SwitchField
              name="approveLink"
              label={t("approvalSteps.approveLink", { recipient })}
              description={t("approvalSteps.approveLinkDesc")}
              value={supervisorNotification.approveLink}
              onChange={handleNotificationLinks}
            />
            {supervisorNotification.approveLink && (
              <Field>
                <TextField
                  name="approveLinkLabel"
                  variant="outlined"
                  label={t("approvalSteps.approveLinkLabel")}
                  value={supervisorNotification.approveLinkLabel}
                  onChange={handleNotificationText}
                  error={approveLinkError}
                  inputProps={{ maxLength: NOTIFICATION_LIMITS.CTA_LABEL }}
                />
                <ErrorMessage
                  isError={approveLinkError}
                  wording={t("approvalSteps.fieldRequired")}
                />
              </Field>
            )}

            <SwitchField
              name="rejectLink"
              label={t("approvalSteps.rejectLink", { recipient })}
              description={t("approvalSteps.rejectLinkDesc")}
              value={supervisorNotification.rejectLink}
              onChange={handleNotificationLinks}
            />
            {supervisorNotification.rejectLink && (
              <Field>
                <TextField
                  name="rejectLinkLabel"
                  variant="outlined"
                  label={t("approvalSteps.rejectLinkLabel")}
                  value={supervisorNotification.rejectLinkLabel}
                  onChange={handleNotificationText}
                  error={rejectLinkError}
                  inputProps={{ maxLength: NOTIFICATION_LIMITS.CTA_LABEL }}
                />
                <ErrorMessage
                  isError={rejectLinkError}
                  wording={t("approvalSteps.fieldRequired")}
                />
              </Field>
            )}
          </FormFields>
        </Box>
      )}
    </Box>
  );
}
